import { useState, useMemo, useEffect, useCallback } from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Stack, Typography, Link, Box, CircularProgress } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ReactComponent as AttachFileIcon } from '@assets/images/icon/ico_download.svg'
import { useIsMobile } from '@/hooks/Responsive'
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useParams } from 'react-router-dom';
import { fetchMyManagementLectureList, fetchMyCourseLectureIntroduce } from '@/api/mypage';
import { getVLabLectureAttachFile } from '@/api/vx'
import { cleanHtml, downloadFile } from '@/api/util'



function formatContentTm(tm) {
  let seconds = Number(tm);
  if (seconds < 60) {
    	return '00:' + seconds
	}
	// sec
	var mins = Math.floor(seconds / 60);
	var secs = seconds - mins*60
	return addZero(mins) + ':' + addZero(secs)
    
	function addZero(num) {
		return ((num < 10) ? '0' : '') + num
	}
}

function IntroductionSection() {
  const { isMobile } = useIsMobile();
  const { id } = useParams();
  const [introData, setIntroData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntro = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await fetchMyCourseLectureIntroduce({ params: { eduLectureId: id } });
        setIntroData(response.data);
      } catch (error) {
        console.error("Failed to fetch introduction data:", error);
        setIntroData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchIntro();
  }, [id]);

  const renderSection = ({ title, list, paragraphs, mb = 3 }) => {
    return (
      <Stack
        spacing={1}
        sx={{ 
          mb
        }}
      >
        {/* 제목 */}
        <Typography fontWeight={600}>{title}</Typography>

        {/* 리스트 */}
        {list?.length > 0 && (
          <Stack
            component="ul"
            sx={{
              pl: 2,
              '& li::marker': {
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                bgcolor: 'grey.600'
              },
            }}
          >
            {list.map((item, idx) => (
              <Typography
                component="li"
                key={idx}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        )}

        {/* 본문 */}
        {Array.isArray(paragraphs)
          ? paragraphs.map((text, idx) => (
              <Typography
                key={idx}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {text}
              </Typography>
            ))
          : (
            <Typography
              sx={{ whiteSpace: 'pre-line' }}
            >
              {paragraphs}
            </Typography>
          )}
      </Stack>
    )
  }

  if (loading) {
    return <Typography sx={{ textAlign: 'center', py: 5 }}>Loading...</Typography>;
  }

  if (!introData) {
    return <Typography sx={{ textAlign: 'center', py: 5 }}>소개 정보를 불러올 수 없습니다.</Typography>;
  }

  return (
    <>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 7,
          pt: 1,
          px: isMobile ? 0 : 3,
        }}
      >
        {renderSection({
          title: '과정소개',
          paragraphs: [<Box dangerouslySetInnerHTML={{ __html: cleanHtml(introData?.eduIntroduce) }} /> || ''],
        })}

        {renderSection({
          title: '과정특징',
          paragraphs: <Box dangerouslySetInnerHTML={{ __html: cleanHtml(introData?.cuationMetters1) }} /> || '',
        })}

        {renderSection({
          title: '학습대상',
          paragraphs: introData.eduTgNop || '',
        })}

        {renderSection({
          title: '강사소개',
          paragraphs: introData.lecturerName || '',
          mb: 0,
        })}
      </Box>
    </>
  )
}

export default function LectureListTab() {
  const { isMobile } = useIsMobile()
  const { id } = useParams();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachFile, setAttachFile] = useState(null)

  function LectureAttachment() {

  if (!attachFile) return null;

  const ext = attachFile.lecturePlanFileName
    ?.split(".")
    .pop();

  const fileName = `강좌계획표.${ext}`;

  return (
    <Stack direction="row" justifyContent="flex-end">

      <Link
        component="button"
        underline="hover"
        onClick={() =>
          downloadFile(
            attachFile.lecturePlanFileUrl,
            fileName
          )
        }
        sx={{
          mt: 0,
          px: 2,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer"
        }}
      >
        <AttachFileIcon />

        <Typography component="span" color="text.primary">
          {fileName}
        </Typography>

      </Link>

    </Stack>
  );
}


  useEffect(() => {
    const getLectureAttachFile = async () => {
              setLoading(true)
              try {
                const response = await getVLabLectureAttachFile({ params: { eduLectureId: id } })
                // The API doesn't exist, so I'll use the hardcoded data as a mock response.
                setAttachFile(response.data)
              } catch (error) {
                console.error('Failed to fetch lecture File:', error)
              } finally {
                setLoading(false)
              }
              
            }
      const fetchLectures = async () => {
          setLoading(true);
          try {
              const response = await fetchMyManagementLectureList({ params: { eduLectureId: id } });//id
              setAllItems(response.data.courseList || []);
          } catch (error) {
              console.error("Failed to fetch lecture list:", error);
              setAllItems([]);
          } finally {
              setLoading(false);
          }
      };

      if (id) {
          getLectureAttachFile();
          fetchLectures();
      }
  }, [id]);

  const grouped = useMemo(() => {
    const result = []
    let currentSection = null

    allItems.forEach((item) => {
      if (item.sortSeq === '0') {
        currentSection = { ...item, lectures: [] }
        result.push(currentSection)
      } else if (item.type === 'survey') {
        if (result.length) result[result.length - 1].survey = item
      } else if (currentSection) {
        currentSection.lectures.push(item)
      }
    })
    return result
  }, [allItems])

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
  }

  if (allItems.length === 0) {
      return <Typography sx={{ p: 5, textAlign: 'center' }}>강의 목록이 없습니다.</Typography>
  }

  return (
    <>
      <IntroductionSection/>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mt: 6 }}>
        <Typography variant="subtitle1" fontWeight={600}>강의 목록</Typography>
        <LectureAttachment isMobile={isMobile} />
      </Stack>

      {grouped.map((section, idx) => (
        <Box key={section.eduSectionId}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
              overflow: 'hidden',
              mt: 1.5,
            }}
          >
            <Accordion defaultExpanded={idx === 0} sx={{ boxShadow: 'none', '&:before': { display: 'none' }}}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 'auto',
                  p: 3,
                  '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
                    m: 0,
                  },
                }}
              >
                <Stack direction="row" alignItems="center">
                  <Typography fontWeight={500}>{String(idx+1).padStart(2,"0")}</Typography>
                  <Box
                    sx={{
                      width: '1px',
                      height: '16px',
                      bgcolor: 'divider',
                      mx: 1,
                    }}
                  />
                  <Typography>{section.eduSectionName}</Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0, px: isMobile ? 2 : 3 }}>
                {section.lectures.map((lec, idx) => (
                  <Stack
                    key={lec.eduCourseId}
                    direction={isMobile ? 'column' : 'row'}
                    justifyContent="space-between"
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    sx={{
                      borderTop: '1px solid', 
                      borderColor: 'divider', 
                      p: 0,
                      py: 3
                    }}
                  >
                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        color={
                          lec.category === '체험형'
                            ? 'secondary2.main'
                            : 'secondary.main'
                        }
                        fontWeight={600}
                      >
                        {lec.category}
                      </Typography>
                      <Typography variant="subtitle3" component="p" fontWeight={600}>
                        {lec.courseName ?? '제목 없음'}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" component="span" color="text.secondary">
                          {lec.lecturerName ?? '강사'}
                        </Typography>

                        <Box sx={{ width: '1px', height: '12px', bgcolor: 'divider' }} />

                        <Typography variant="body2" component="span" color="text.secondary">
                          {lec.contentType ?? 'PC 기반 실행'}
                        </Typography>

                        <Box sx={{ width: '1px', height: '12px', bgcolor: 'divider' }} />

                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTimeIcon fontSize="15" sx={{ color: 'text.secondary' }} />
                          <Typography variant="body2" component="span" color="text.secondary">
                            {lec?.contentTm ? formatContentTm(lec?.contentTm): '00:00'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      ))}
    </>
  )
}

